import { Box, BoxProps } from '@chakra-ui/react'
import { type UniqueIdentifier, useDroppable } from '@dnd-kit/core'
import { SortableContext } from '@dnd-kit/sortable'
import React from 'react'
import type { ReactNode } from 'react'

import { SortableItem } from './SortableItem'

type BaseItem = {
  id: UniqueIdentifier
}

type SortableListProps<T extends BaseItem> = {
  id?: string
  items: T[]
  renderItem(item: T): ReactNode
} & BoxProps

export function SortableList<T extends BaseItem>({
  id,
  items,
  renderItem,
  ...props
}: SortableListProps<T>) {
  // Seems redundant with SortableContext, but this is required to create a working drop zone when the list is empty
  const { setNodeRef } = useDroppable({
    id: id ?? 'sortable-list',
    data: {
      sortable: {
        containerId: id,
      },
    },
  })

  return (
    <SortableContext id={id} items={items}>
      <Box
        as="ul"
        className="SortableList"
        role="application"
        ref={setNodeRef}
        {...props}
      >
        {items.map(item => (
          <React.Fragment key={item.id}>{renderItem(item)}</React.Fragment>
        ))}
      </Box>
    </SortableContext>
  )
}

SortableList.Item = SortableItem
